import { booleanAttr, extractPlainText, rawAttr, stringAttr } from "@wiki/core";
import { Fragment, Slice, type Mark, type Node as ProseMirrorNode } from "prosemirror-model";
import { Plugin, PluginKey, type EditorState, type Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

import type { EditorContext } from "../context";
import type { CommentAnchorSelection } from "../types";
import { marks } from "../schema";

/** Every comment id anchored anywhere in the document. */
export function commentIdsIn(doc: ProseMirrorNode): Set<string> {
  const ids = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      if (mark.type === marks.comment) {
        const id = rawAttr(mark, "id");
        if (typeof id === "string" && id !== "") ids.add(id);
      }
    }
    return true;
  });
  return ids;
}

/** Every comment id anchored in the document, in document order, de-duplicated. */
export function orderedCommentIds(doc: ProseMirrorNode): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      if (mark.type !== marks.comment) continue;
      const id = rawAttr(mark, "id");
      if (typeof id === "string" && id !== "" && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    return true;
  });
  return ids;
}

/**
 * Draft anchors written by this user and never submitted (§ edge case 7). A tab
 * closed with a composer open leaves one behind; the client that owns it removes
 * it the next time the document loads, and ignores everyone else's.
 */
export function draftCommentIds(doc: ProseMirrorNode, userId: string): string[] {
  const ids = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      if (mark.type !== marks.comment) continue;
      if (!booleanAttr(mark, "draft")) continue;
      if (stringAttr(mark, "userId") !== userId) continue;
      const id = stringAttr(mark, "id");
      if (id !== null && id !== "") ids.add(id);
    }
    return true;
  });
  return [...ids];
}

/** The document range a comment id covers, or null when it anchors nothing. */
export function commentRange(
  doc: ProseMirrorNode,
  id: string,
): { from: number; to: number } | null {
  let from: number | null = null;
  let to = 0;
  doc.descendants((node, pos) => {
    const anchored = node.marks.some(
      (mark) => mark.type === marks.comment && stringAttr(mark, "id") === id,
    );
    if (anchored) {
      from ??= pos;
      to = Math.max(to, pos + node.nodeSize);
    }
    return true;
  });
  return from === null ? null : { from, to };
}

/**
 * What the user selected, expressed the way the anchoring API wants it
 * (`docs/spec/comments.md` §6): the quoted text plus enough of what surrounds it
 * to pick this occurrence out of a document that says the same thing twice.
 */
export const ANCHOR_CONTEXT_CHARS = 40;

export function anchorSelection(state: EditorState): CommentAnchorSelection {
  const { from, to, empty } = state.selection;
  if (empty) {
    return { empty: true, from, to, text: "", prefix: "", suffix: "" };
  }
  const text = state.doc.textBetween(from, to, "\n");
  const prefixFrom = Math.max(0, from - ANCHOR_CONTEXT_CHARS);
  const suffixTo = Math.min(state.doc.content.size, to + ANCHOR_CONTEXT_CHARS);
  return {
    empty: false,
    from,
    to,
    text,
    prefix: state.doc.textBetween(prefixFrom, from, "\n"),
    suffix: state.doc.textBetween(to, suffixTo, "\n"),
  };
}

/** The plain text an anchored range covers right now, for the sidebar's quote. */
export function anchorTextFor(doc: ProseMirrorNode, id: string): string | null {
  const range = commentRange(doc, id);
  return range === null ? null : doc.textBetween(range.from, range.to, "\n");
}

/** Unused by the editor itself; exported so the comments UI can size a body. */
export function plainTextOf(doc: ProseMirrorNode): string {
  return extractPlainText(doc);
}

/**
 * Writes the anchor for a comment the local user is composing (§4). The id is
 * generated before the comment exists, which is what lets one round trip create
 * the mark and the comment together.
 */
export function applyCommentMark(
  view: EditorView,
  options: { id: string; userId: string; draft?: boolean; from?: number; to?: number },
): boolean {
  const { state } = view;
  const from = options.from ?? state.selection.from;
  const to = options.to ?? state.selection.to;
  if (from >= to) return false;
  const mark = marks.comment.create({
    id: options.id,
    userId: options.userId,
    resolved: false,
    draft: options.draft ?? false,
  });
  view.dispatch(state.tr.addMark(from, to, mark));
  return true;
}

/** Rewrites one attribute of a comment mark wherever it appears. */
function mapCommentMark(
  view: EditorView,
  id: string,
  change: ((mark: Mark) => Mark | null) | null,
): void {
  const { state } = view;
  const tr: Transaction = state.tr;
  let touched = false;
  state.doc.descendants((node, pos) => {
    for (const mark of node.marks) {
      if (mark.type !== marks.comment || stringAttr(mark, "id") !== id) continue;
      const to = pos + node.nodeSize;
      tr.removeMark(pos, to, mark);
      const next = change === null ? null : change(mark);
      if (next !== null) tr.addMark(pos, to, next);
      touched = true;
    }
    return true;
  });
  if (touched) {
    tr.setMeta("addToHistory", false);
    view.dispatch(tr);
  }
}

/** Abandoning a composer takes its draft anchor with it (§4). */
export function removeCommentMark(view: EditorView, id: string): void {
  mapCommentMark(view, id, null);
}

/** On submit the anchor stops being a draft; on resolve the highlight fades (§9). */
export function setCommentMarkAttrs(
  view: EditorView,
  id: string,
  attrs: { draft?: boolean; resolved?: boolean },
): void {
  mapCommentMark(view, id, (mark) =>
    marks.comment.create({
      ...mark.attrs,
      ...(attrs.draft === undefined ? {} : { draft: attrs.draft }),
      ...(attrs.resolved === undefined ? {} : { resolved: attrs.resolved }),
    }),
  );
}

/**
 * The gutter (§15). With `commentsInGutter` on, every block that carries an
 * anchor gets a dot in the left margin; clicking it focuses that thread. It is a
 * separate affordance from the underline and can be turned off on its own.
 */
export function commentGutterPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiCommentGutter"),
    props: {
      decorations(state) {
        if (!context.commentsInGutter) return null;
        const decorations: Decoration[] = [];
        state.doc.forEach((block, offset) => {
          let id: string | null = null;
          block.descendants((node) => {
            if (id !== null) return false;
            for (const mark of node.marks) {
              if (mark.type === marks.comment && !booleanAttr(mark, "draft")) {
                id = stringAttr(mark, "id") ?? null;
              }
            }
            return true;
          });
          if (id === null) return;
          const anchored: string = id;
          decorations.push(
            Decoration.widget(
              offset + 1,
              () => {
                const dot = document.createElement("button");
                dot.type = "button";
                dot.className = "wiki-comment-gutter";
                dot.setAttribute("data-comment-id", anchored);
                dot.setAttribute("aria-label", "Show comment");
                dot.addEventListener("mousedown", (event) => {
                  event.preventDefault();
                  context.onCommentClick(anchored);
                });
                return dot;
              },
              { side: -1, key: `gutter-${anchored}` },
            ),
          );
        });
        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}

/**
 * Comment anchors and their two paste rules (`docs/spec/comments.md` §4):
 *
 * - a span carrying another document's id is rejected outright, so copying a
 *   commented paragraph into another page does not drag the discussion along;
 * - a mark whose id is *still in this document* is dropped from the pasted
 *   copy, which is what makes cut-and-paste keep an anchor while copy-and-paste
 *   loses it. A comment must anchor exactly one range.
 *
 * The mark itself is created by the comments UI, never here; this plugin only
 * renders anchors, routes clicks, and polices pasted ones.
 */
export function commentsPlugin(context: EditorContext): Plugin {
  return new Plugin({
    key: new PluginKey("wikiComments"),
    props: {
      markViews: {
        comment: (mark) => {
          const dom = document.createElement("span");
          const id = stringAttr(mark, "id") ?? "";
          const resolved = booleanAttr(mark, "resolved");
          const draft = booleanAttr(mark, "draft");
          dom.id = `comment-${id}`;
          dom.className = [
            "wiki-comment",
            resolved ? "wiki-comment--resolved" : "",
            draft ? "wiki-comment--draft" : "",
          ]
            .filter((entry) => entry !== "")
            .join(" ");
          dom.setAttribute("data-comment-id", id);
          dom.setAttribute("data-user-id", stringAttr(mark, "userId") ?? "");
          if (resolved) dom.setAttribute("data-resolved", "true");
          if (draft) dom.setAttribute("data-draft", "true");
          if (context.documentId !== null) {
            dom.setAttribute("data-document-id", context.documentId);
          }
          return { dom };
        },
      },
      handleClickOn(view, _pos, _node, _nodePos, event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return false;
        const anchor = target.closest("[data-comment-id]");
        if (!(anchor instanceof HTMLElement)) return false;
        const id = anchor.getAttribute("data-comment-id");
        if (id === null || id === "") return false;
        if (
          anchor.getAttribute("data-resolved") === "true" &&
          anchor.getAttribute("data-draft") !== "true"
        ) {
          // A resolved anchor is inert unless the resolved view is open, which
          // is the sidebar's business: it still gets told, and decides.
          context.onCommentClick(id);
          return false;
        }
        context.onCommentClick(id);
        view.focus();
        return true;
      },
      transformPastedHTML(html) {
        if (context.documentId === null) return html;
        // Reject anchors that belong to another document before parsing.
        return html.replace(
          /<span([^>]*?)data-document-id="([^"]*)"([^>]*)>/g,
          (whole, before: string, documentId: string, after: string) =>
            documentId === context.documentId
              ? whole
              : `<span${before.replace(/data-comment-id="[^"]*"/, "")}${after.replace(
                  /data-comment-id="[^"]*"/,
                  "",
                )}>`,
        );
      },
      transformPasted(slice, view) {
        const existing = commentIdsIn(view.state.doc);
        if (existing.size === 0) return slice;
        const cleaned = removeCommentMarks(slice.content, (id) => existing.has(id));
        return new Slice(cleaned, slice.openStart, slice.openEnd);
      },
    },
  });
}

/** Drop comment marks whose id `shouldRemove` reports as already anchored. */
export function removeCommentMarks(
  fragment: Fragment,
  shouldRemove: (id: string) => boolean,
): Fragment {
  const children: ProseMirrorNode[] = [];
  fragment.forEach((child) => {
    const kept = child.marks.filter(
      (mark) => mark.type !== marks.comment || !shouldRemove(stringAttr(mark, "id") ?? ""),
    );
    const next = child.isText
      ? child.mark(kept)
      : child.copy(removeCommentMarks(child.content, shouldRemove)).mark(kept);
    children.push(next);
  });
  return Fragment.fromArray(children);
}

/** Used by the comments UI to find the range a mark covers. */
export function commentMarkAt(state: EditorState, pos: number): Mark | null {
  const $pos = state.doc.resolve(pos);
  const mark = marks.comment.isInSet($pos.marks());
  return mark ?? null;
}
